import { FormattedMessage } from 'react-intl';
import { Fragment, memo } from 'react';
import { Button, Heading, HStack, Text, Tooltip, VStack } from '@library';
import type { ProductVehicle } from '../types';

import { ProgressSpinner } from '../../core/components/ProgressIndicators';
import styles from './MotorcycleSearchMetadata.module.scss';

const MotorcycleSearchMetadata = ({
    vehicle,
    showTitle,
}: {
    vehicle?: ProductVehicle;
    showTitle?: boolean;
}) => {
    if (!vehicle) return <ProgressSpinner />;

    const limit = 4;

    const firstFewTypeAcceptances = vehicle.typeAcceptanceList.slice(0, limit);
    const remainingTypeAcceptances = vehicle.typeAcceptanceList.slice(limit);

    const firstFewTypeNumbers = vehicle.typeNumbers.slice(0, limit);
    const remainingTypeNumbers = vehicle.typeNumbers.slice(limit);

    return (
        <div className={styles.motorcycleSearchMetadata}>
            <VStack gap="micro">
                {showTitle && (
                    <Text medium secondary>
                        <FormattedMessage id="Motorcycle Search Currently Selected Model" />
                    </Text>
                )}
                <Heading h3 bold dataTestClass="modelAndManufacturerName">
                    {vehicle.model} {vehicle.manufacturerName}
                </Heading>

                <HStack gap="small" wrap>
                    <Text medium secondary data-test-class={'ratedPower'}>
                        <FormattedMessage id="kw" />: {vehicle.ratedPower}
                    </Text>
                    <Text medium secondary data-test-class={'displacement'}>
                        <FormattedMessage id="ccm" />: {vehicle.ccm}
                    </Text>
                    <Text medium secondary data-test-class={'year'}>
                        <FormattedMessage id="modelYear" />:{' '}
                        {vehicle.manufacturingStartYear} -{' '}
                        {vehicle.manufacturingEndYear || ''}
                    </Text>
                    <HStack data-test-class={'type'}>
                        <Text medium secondary>
                            <FormattedMessage id="type" />:
                        </Text>
                        &nbsp;
                        <HStack>
                            {firstFewTypeNumbers.map((typeNumber, idx) => (
                                <Fragment key={typeNumber}>
                                    <Text secondary regular>
                                        {typeNumber}
                                    </Text>
                                    {idx !== firstFewTypeNumbers.length - 1 && (
                                        <>,&nbsp;</>
                                    )}
                                </Fragment>
                            ))}
                            {remainingTypeNumbers.length > 0 && <>, </>}
                            {remainingTypeNumbers.length > 0 && (
                                <Tooltip
                                    mode="click"
                                    content={
                                        <HStack wrap gap="mini">
                                            {remainingTypeNumbers.map(item => (
                                                <Text
                                                    key={item}
                                                    secondary
                                                    regular
                                                >
                                                    {item}
                                                </Text>
                                            ))}
                                        </HStack>
                                    }
                                    showArrow
                                    modal
                                >
                                    <div>
                                        &nbsp;
                                        <Button text inline>
                                            <Text secondary>
                                                <FormattedMessage
                                                    id="+{count} more"
                                                    values={{
                                                        count: remainingTypeNumbers.length,
                                                    }}
                                                />
                                            </Text>
                                        </Button>
                                    </div>
                                </Tooltip>
                            )}
                        </HStack>
                    </HStack>
                    <HStack data-test-class={'typeApprovalList'}>
                        <Text medium secondary>
                            <FormattedMessage id="typeApprovalList" />:
                        </Text>
                        &nbsp;
                        <HStack>
                            {firstFewTypeAcceptances.map(
                                (typeAcceptance, idx) => (
                                    <Fragment key={typeAcceptance}>
                                        <Text secondary regular>
                                            {typeAcceptance}
                                        </Text>
                                        {idx !==
                                            firstFewTypeAcceptances.length -
                                                1 && <>,&nbsp;</>}
                                    </Fragment>
                                )
                            )}

                            {remainingTypeAcceptances.length > 0 && <>, </>}
                            {remainingTypeAcceptances.length > 0 && (
                                <Tooltip
                                    modal
                                    mode="click"
                                    content={
                                        <HStack wrap gap="mini">
                                            {remainingTypeAcceptances.map(
                                                item => (
                                                    <Text
                                                        key={item}
                                                        secondary
                                                        regular
                                                    >
                                                        {item}
                                                    </Text>
                                                )
                                            )}
                                        </HStack>
                                    }
                                    showArrow
                                >
                                    <div>
                                        &nbsp;
                                        <Button text inline>
                                            <Text secondary>
                                                <FormattedMessage
                                                    id="+{count} more"
                                                    values={{
                                                        count: remainingTypeAcceptances.length,
                                                    }}
                                                />
                                            </Text>
                                        </Button>
                                    </div>
                                </Tooltip>
                            )}
                        </HStack>
                    </HStack>
                </HStack>
            </VStack>
        </div>
    );
};

export default memo(MotorcycleSearchMetadata);
